import React, {useEffect, useMemo, useRef} from "react";
import * as d3 from "d3";
import {EmployeeVisitType,} from "../../api/EmployeeVisitStore";
import {OfficeVisitSheet} from "../../api/Model";
import {workDayVisitCounter} from "./EmployeeVisitReportFilter";
import {visitHeatMapPercentLabel, visitHeatMapRowLabel} from "./VisitHeatMapRowLabel";

const visitColor: Record<EmployeeVisitType, string> = {
    [EmployeeVisitType.WorkDayVisit]: '#4CAF50',
    [EmployeeVisitType.WeekendVisit]: "#b8e6ba",
    [EmployeeVisitType.Absent]: "#F8F8F8",
    [EmployeeVisitType.BeforeStart]: "#FFFFFF",
};

export function VisitHeatMap<Row extends {fullName: string, company?: string}>({data}: {
    data: OfficeVisitSheet<Row, EmployeeVisitType>,
}) {
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);

    const {cellWidth, cellHeight, margin} = useMemo(() => {
        const longestRowLabel = data.rows.reduce(
            (length, row) => Math.max(length, visitHeatMapRowLabel(row.value).length),
            0,
        );
        return {
            cellWidth: 12,
            cellHeight: 12,
            margin: {
                top: 50,
                right: 100,
                bottom: 100,
                left: Math.max(150, Math.min(320, longestRowLabel * 7 + 15)),
            },
        };
    }, [data.rows]);

    useEffect(() => {
        if (!data || !data.dates || !data.rows || data.dates.length === 0 || data.rows.length === 0) {
            return;
        }

        const canvas = canvasRef.current;
        const svgEl = svgRef.current;
        if (!canvas || !svgEl) return;

        // Compute layout
        const width = Math.max(data.dates.length * cellWidth, 800) - margin.left - margin.right;
        const plotHeight = data.rows.length * cellHeight;
        const totalWidth = width + margin.left + margin.right;
        const totalHeight = plotHeight + margin.top + margin.bottom;

        // Scales
        const rowLabels = data.rows.map(row => visitHeatMapRowLabel(row.value));

        const x = d3.scaleBand()
            .domain(data.dates)
            .range([0, width])
            .padding(0.05);

        const y = d3.scaleBand()
            .domain(rowLabels)
            .range([0, plotHeight])
            .padding(0.05);

        // Canvas sizing (use DPR for crisp drawing)
        const dpr = window.devicePixelRatio || 1;
        canvas.style.width = `${totalWidth}px`;
        canvas.style.height = `${totalHeight}px`;
        canvas.width = Math.floor(totalWidth * dpr);
        canvas.height = Math.floor(totalHeight * dpr);

        // SVG sizing
        const svg = d3.select(svgEl)
            .attr("width", totalWidth)
            .attr("height", totalHeight);

        // Render axes in SVG (low DOM count)
        const root = svg
            .selectAll<SVGGElement, null>("g.chart-root")
            .data([null])
            .join("g")
            .attr("class", "chart-root")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        const maxXTicks = 30;
        const tickEvery = Math.max(1, Math.ceil(data.dates.length / maxXTicks));
        const xTickValues = data.dates.filter((_, i) => i % tickEvery === 0);

        root.selectAll<SVGGElement, null>("g.x-axis")
            .data([null])
            .join("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0,${plotHeight})`)
            .call(d3.axisBottom(x).tickValues(xTickValues))
            .call(g => g.selectAll("text")
                .style("text-anchor", "end")
                .attr("dx", "-.8em")
                .attr("dy", ".15em")
                .attr("transform", "rotate(-65)")
            );

        root.selectAll<SVGGElement, null>("g.y-axis")
            .data([null])
            .join("g")
            .attr("class", "y-axis")
            .call(d3.axisLeft(y));

        // Right-side percentages (SVG text is fine: one per row)
        const countWorkDayVisitPercent = workDayVisitCounter(data.dates);
        const rowStats = data.rows.map((row) => {
            const rLabel = visitHeatMapRowLabel(row.value);
            const pct = countWorkDayVisitPercent(row).percent;
            return {key: rLabel, row: rLabel, pct};
        });

        root.selectAll<SVGTextElement, typeof rowStats[number]>("text.row-pct")
            .data(rowStats, d => d.key)
            .join("text")
            .attr("class", "row-pct")
            .attr("x", width + 10)
            .attr("y", d => (y(d.row) ?? 0))
            .attr("dy", y.bandwidth() / 2 + 4)
            .style("text-anchor", "start")
            .style("font-size", "12px")
            .text(d => visitHeatMapPercentLabel(d.pct));

        // Draw cells on Canvas (fast)
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Avoid forced sync layout by drawing on next frame
        const raf = requestAnimationFrame(() => {
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.clearRect(0, 0, totalWidth, totalHeight);

            // Shift by margins (so canvas aligns with axes group)
            ctx.save();
            ctx.translate(margin.left, margin.top);

            const bw = x.bandwidth();
            const bh = y.bandwidth();

            // Precompute x positions once
            const xPos = data.dates.map(d => x(d) ?? 0);

            // Fill cells
            for (let i = 0; i < data.rows.length; i++) {
                const rLabel = rowLabels[i];
                const yy = y(rLabel);
                if (yy == null) continue;

                const visits = data.rows[i].visits;
                for (let j = 0; j < data.dates.length; j++) {
                    const xx = xPos[j];
                    const visit = visits[j];
                    ctx.fillStyle = visitColor[visit];
                    ctx.fillRect(xx, yy, bw, bh);
                }
            }

            // // Optional grid lines (kept simple; you can remove if you want even faster)
            // ctx.strokeStyle = "#ffffff";
            // ctx.lineWidth = 1;
            // for (let i = 0; i < data.rows.length; i++) {
            //     const rLabel = rowLabels[i];
            //     const yy = y(rLabel);
            //     if (yy == null) continue;
            //     for (let j = 0; j < data.dates.length; j++) {
            //         const xx = xPos[j];
            //         ctx.strokeRect(xx, yy, bw, bh);
            //     }
            // }

            ctx.restore();
        });

        return () => cancelAnimationFrame(raf);
    }, [data, cellWidth, cellHeight, margin]);

    return (
        <div
            ref={wrapperRef}
            style={{
                position: "relative",
                maxWidth: "100%",
                overflow: "auto",
            }}
        >
            <canvas
                ref={canvasRef}
                style={{
                    display: "block",
                }}
            />
            <svg
                ref={svgRef}
                style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    pointerEvents: "none",
                    display: "block",
                }}
            />
        </div>
    );
}
